import apiClient from './client'
import { extensionForFormat, filenameFromDisposition } from '../utils/download'
import type {
  DateOverride,
  DateOverrideResult,
  DeleteOverrideResult,
  SchedulerConfig,
  WeeklyRule,
  WeeklyRulesResult,
} from './scheduler'

// ── Scheduling-data export ────────────────────────────────────────────────────

export type ExportFormat = 'csv' | 'xlsx' | 'pdf'

/** How the export runs. `auto` lets the backend decide from a row-count estimate. */
export type ExportMode = 'auto' | 'sync' | 'background'

export interface SessionExportPayload {
  format: ExportFormat
  mode?: ExportMode
  /** Same filter names as GET /admin/sessions, so exports match the visible list. */
  status?: string
  search?: string
  interviewer_id?: number
  from_date?: string
  to_date?: string
  /** IANA zone for the local date/time columns. */
  timezone?: string
  columns?: string[]
}

export interface ExportColumnOption {
  key: string
  header: string
  default: boolean
}

export type ExportJobStatus = 'queued' | 'running' | 'success' | 'failed'

export interface ExportJob {
  id: string
  status: ExportJobStatus
  export_format: ExportFormat
  timezone_name: string
  row_count: number | null
  truncated: boolean
  file_name: string | null
  byte_size: number | null
  error_message: string | null
  created_at: string
  started_at: string | null
  completed_at: string | null
  expires_at: string | null
}

export interface ExportJobAccepted {
  job_id: string
  status: 'queued' | 'running'
  deduplicated: boolean
  message: string
}

/** A small export comes back as a file; a large one as a job to poll. */
export type SessionExportResult =
  | { kind: 'file'; blob: Blob; filename: string }
  | { kind: 'job'; job: ExportJobAccepted }

export interface RBACPermission {
  module: string
  can_view: boolean
  can_create: boolean
  can_edit: boolean
  can_delete: boolean
}

export interface RBACRole {
  id: number
  name: string
  description: string | null
  is_system: boolean
  hierarchy_level: number
  created_at: string
  permissions: RBACPermission[]
}

export interface AdminUserRoleItem {
  user_id: number
  user_email: string | null
  role_id: number | null
  role_name: string | null
  is_super_admin: boolean
}

export interface MyPermissions {
  is_super_admin: boolean
  hierarchy_level: number
  permissions: Record<string, { can_view: boolean; can_create: boolean; can_edit: boolean; can_delete: boolean }>
}

export interface QuestionTypeItem {
  id: number
  name: string
  slug: string
  description: string | null
  display_order: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface QuestionTypeCategoryItem {
  id: number
  question_type_id: number
  key: string
  label: string
  description: string
  display_order: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface PayTierItem {
  id: number
  name: string
  label: string
  rate: number
  description: string | null
  is_active: boolean
  sort_order: number
  created_at: string
  updated_at: string
}

// ── Admin Scheduling Console (booking actions, filters, change requests) ──────

export interface AdminInterviewerOption {
  interviewer_id: number
  alias: string | null
}

export interface AdminRescheduleHistoryItem {
  sequence_no: number
  previous_start_utc: string
  previous_end_utc: string
  new_start_utc: string
  new_end_utc: string
  previous_interviewer_id: number | null
  new_interviewer_id: number | null
  rescheduled_by_user_id: number | null
  rescheduled_by_role: string | null
  reason: string | null
  created_at: string
}

export interface AdminChangeRequestItem {
  id: number
  booking_id: number
  requested_by_user_id: number
  interviewer_id: number | null
  interviewer_alias: string | null
  client_name: string | null
  client_email: string | null
  type: string // 'cancel' | 'reschedule'
  reason: string | null
  // pending  — awaiting an admin decision
  // approved — admin agreed; the cancel/reschedule has NOT happened yet and the
  //            admin still has to perform it on the session row. Still "open".
  // resolved — the action was carried out (or the session was cancelled/reassigned)
  // rejected — declined; the interviewer keeps the session
  status: string
  admin_notes: string | null
  resolved_by_user_id: number | null
  scheduled_start_utc: string | null
  booking_status: string | null
  created_at: string
  updated_at: string
}

// One interviewer availability-extension request in the admin review queue.
// A ROLLING grant of extra_days on top of the interviewer's configured window
// (total capped at 60) — not an end date. Mirrors AdminChangeRequestItem.
export interface AvailabilityExtensionItem {
  id: number
  interviewer_id: number
  interviewer_alias: string | null
  extra_days: number
  status: 'pending' | 'approved' | 'rejected' | 'revoked'
  reason: string | null
  admin_note: string | null
  decided_by: number | null
  decided_at: string | null
  created_at: string
  updated_at: string
}

export const adminApi = {
  getStats: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/stats', { params }).then((r) => r.data.data ?? r.data),

  getRecentSessions: (limit = 8) =>
    apiClient.get('/admin/recent-sessions', { params: { limit } }).then((r) => r.data.data ?? r.data),

  getUsers: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/users', { params }).then((r) => r.data),

  createUser: (data: Record<string, unknown>) =>
    apiClient.post('/admin/users', data).then((r) => r.data.data ?? r.data),

  updateUser: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/admin/users/${id}`, data).then((r) => r.data),

  deleteUser: (id: number) =>
    apiClient.delete(`/admin/users/${id}`).then((r) => r.data),

  setInterviewerAvailability: (userId: number, is_available: boolean) =>
    apiClient
      .put(`/admin/users/${userId}/availability`, { is_available })
      .then((r) => r.data.data ?? r.data),

  getApplications: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/applications', { params }).then((r) => r.data.data ?? r.data),

  getApplication: (id: number) =>
    apiClient.get(`/admin/applications/${id}`).then((r) => r.data),

  approveApplication: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/admin/applications/${id}/approve`, data).then((r) => r.data),

  declineApplication: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/admin/applications/${id}/decline`, data).then((r) => r.data),

  getSessions: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/sessions', { params }).then((r) => r.data),

  getSessionCounts: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/sessions/counts', { params }).then((r) => r.data.data ?? r.data),

  // ── Admin Scheduling Console ────────────────────────────────────────────────
  getSessionInterviewers: () =>
    apiClient
      .get('/admin/sessions/interviewers')
      .then((r) => (r.data.data ?? r.data) as AdminInterviewerOption[]),

  /** @deprecated Legacy CSV-only export. Use `createSessionExport` instead. */
  exportSessions: (params?: Record<string, unknown>) =>
    apiClient
      .get('/admin/sessions/export', { params, responseType: 'blob' })
      .then((r) => r.data as Blob),

  // ── Multi-format scheduling-data export ─────────────────────────────────────

  getExportColumns: () =>
    apiClient
      .get('/admin/sessions/export/columns')
      .then((r) => (r.data.data ?? r.data) as ExportColumnOption[]),

  /**
   * Request an export. Resolves to either the finished file (small result sets,
   * HTTP 200) or a job to poll (large ones, HTTP 202).
   *
   * `arraybuffer` rather than `blob` so both the 202 JSON body and an error body
   * can be decoded synchronously — see `decodeBinaryBody` in utils/apiError.
   */
  createSessionExport: (payload: SessionExportPayload): Promise<SessionExportResult> =>
    apiClient
      .post('/admin/sessions/export', payload, { responseType: 'arraybuffer' })
      .then((r) => {
        const buffer = r.data as ArrayBuffer

        if (r.status === 202) {
          const accepted = JSON.parse(
            new TextDecoder().decode(new Uint8Array(buffer)),
          ) as ExportJobAccepted
          return { kind: 'job' as const, job: accepted }
        }

        const contentType =
          (r.headers['content-type'] as string) || 'application/octet-stream'
        return {
          kind: 'file' as const,
          blob: new Blob([buffer], { type: contentType }),
          filename: filenameFromDisposition(
            r.headers['content-disposition'] as string | undefined,
            `sessions.${extensionForFormat(payload.format)}`,
          ),
        }
      }),

  getExportJob: (jobId: string) =>
    apiClient
      .get(`/admin/exports/${jobId}`)
      .then((r) => (r.data.data ?? r.data) as ExportJob),

  listExportJobs: (limit = 10) =>
    apiClient
      .get('/admin/exports', { params: { limit } })
      .then((r) => (r.data.data ?? r.data) as ExportJob[]),

  downloadExportJob: (jobId: string, fallbackName = 'sessions-export') =>
    apiClient
      .get(`/admin/exports/${jobId}/download`, { responseType: 'arraybuffer' })
      .then((r) => ({
        blob: new Blob([r.data as ArrayBuffer], {
          type: (r.headers['content-type'] as string) || 'application/octet-stream',
        }),
        filename: filenameFromDisposition(
          r.headers['content-disposition'] as string | undefined,
          fallbackName,
        ),
      })),

  getSessionHistory: (bookingId: number) =>
    apiClient
      .get(`/admin/sessions/booking/${bookingId}/history`)
      .then((r) => (r.data.data ?? r.data) as AdminRescheduleHistoryItem[]),

  rescheduleBooking: (bookingId: number, data: { new_scheduled_at: string; reason?: string }) =>
    apiClient
      .patch(`/admin/sessions/booking/${bookingId}/reschedule`, data)
      .then((r) => r.data.data ?? r.data),

  reassignBooking: (
    bookingId: number,
    data: { new_interviewer_id: number; new_scheduled_at?: string; reason?: string },
  ) =>
    apiClient
      .patch(`/admin/sessions/booking/${bookingId}/reassign`, data)
      .then((r) => r.data.data ?? r.data),

  cancelBooking: (bookingId: number, data: { reason?: string }) =>
    apiClient
      .post(`/admin/sessions/booking/${bookingId}/cancel`, data)
      .then((r) => r.data.data ?? r.data),

  // `opts` is only meaningful for status='no_show': `no_show_party` records WHO
  // failed to attend (the backend defaults it to 'both') and `reason` is shown to
  // both parties in the notification email. The backend rejects no_show_party on
  // status='completed'. Both are optional, so existing single-argument callers are
  // unaffected.
  setBookingStatus: (
    bookingId: number,
    status: 'completed' | 'no_show',
    opts?: { no_show_party?: 'client' | 'interviewer' | 'both'; reason?: string },
  ) =>
    apiClient
      .patch(`/admin/sessions/booking/${bookingId}/status`, { status, ...opts })
      .then((r) => r.data.data ?? r.data),

  getChangeRequests: (params?: { status?: string }) =>
    apiClient
      .get('/admin/change-requests', { params })
      .then((r) => (r.data.data ?? r.data) as AdminChangeRequestItem[]),

  resolveChangeRequest: (
    requestId: number,
    data: { action: 'approve' | 'reject'; admin_notes?: string },
  ) =>
    apiClient
      .patch(`/admin/change-requests/${requestId}`, data)
      .then((r) => (r.data.data ?? r.data) as AdminChangeRequestItem),

  // ── Availability extensions (beyond 45 days, ≤60) ─────────────────────────
  listAvailabilityExtensions: (params?: { status?: string }) =>
    apiClient
      .get('/admin/scheduler/extension-requests', { params })
      .then((r) => (r.data.data ?? r.data) as AvailabilityExtensionItem[]),

  // Maps a single action to the matching approve/reject/revoke POST. On approve
  // an admin may trim extra_days to grant FEWER days than were requested.
  resolveAvailabilityExtension: (
    id: number,
    data: {
      action: 'approve' | 'reject' | 'revoke'
      admin_note?: string
      extra_days?: number
    },
  ) => {
    const body: { admin_note?: string; extra_days?: number } = {}
    if (data.admin_note !== undefined) body.admin_note = data.admin_note
    if (data.action === 'approve' && data.extra_days)
      body.extra_days = data.extra_days
    return apiClient
      .post(`/admin/scheduler/extension-requests/${id}/${data.action}`, body)
      .then((r) => (r.data.data ?? r.data) as AvailabilityExtensionItem)
  },

  directGrantAvailabilityExtension: (data: {
    interviewer_id: number
    extra_days: number
    admin_note?: string
  }) =>
    apiClient
      .post('/admin/scheduler/extension-requests/direct-grant', data)
      .then((r) => (r.data.data ?? r.data) as AvailabilityExtensionItem),

  // ── Per-interviewer availability & config (feature #9) ────────────────────
  // Admin views/edits ONE interviewer's scheduler on their behalf, keyed on
  // interviewer_profiles.id (the AdminInterviewerOption.interviewer_id). Config
  // PUT accepts admin_locked (server-controlled everywhere else) and the two
  // Part B tunables (booking_window_days ≤60, min_notice_hours ≤720). Rule /
  // override mutations are WARN-BUT-ALLOW: they succeed and may carry a `warning`.
  getInterviewerConfig: (interviewerId: number) =>
    apiClient
      .get(`/admin/scheduler/interviewers/${interviewerId}/config`)
      .then((r) => (r.data.data ?? r.data) as SchedulerConfig),

  setInterviewerConfig: (
    interviewerId: number,
    config: Partial<SchedulerConfig> & { admin_locked?: boolean },
  ) =>
    apiClient
      .put(`/admin/scheduler/interviewers/${interviewerId}/config`, config)
      .then((r) => (r.data.data ?? r.data) as SchedulerConfig),

  getInterviewerRules: (interviewerId: number) =>
    apiClient
      .get(`/admin/scheduler/interviewers/${interviewerId}/availability/rules`)
      .then((r) => {
        const d = r.data?.data ?? r.data
        return (d?.rules ?? d ?? []) as WeeklyRule[]
      }),

  setInterviewerRules: (
    interviewerId: number,
    rules: WeeklyRule[],
  ): Promise<WeeklyRulesResult> =>
    apiClient
      .put(`/admin/scheduler/interviewers/${interviewerId}/availability/rules`, {
        rules,
      })
      .then((r) => {
        const d = r.data?.data ?? r.data
        return {
          rules: (d?.rules ?? d ?? []) as WeeklyRule[],
          warning: (d?.warning ?? null) as string | null,
        }
      }),

  getInterviewerOverrides: (interviewerId: number) =>
    apiClient
      .get(`/admin/scheduler/interviewers/${interviewerId}/availability/overrides`)
      .then((r) => {
        const d = r.data?.data ?? r.data
        return (d?.overrides ?? d ?? []) as DateOverride[]
      }),

  createInterviewerOverride: (
    interviewerId: number,
    override: Omit<DateOverride, 'id'>,
  ): Promise<DateOverrideResult> =>
    apiClient
      .post(
        `/admin/scheduler/interviewers/${interviewerId}/availability/overrides`,
        override,
      )
      .then((r) => (r.data?.data ?? r.data) as DateOverrideResult),

  deleteInterviewerOverride: (
    interviewerId: number,
    overrideId: number,
  ): Promise<DeleteOverrideResult> =>
    apiClient
      .delete(
        `/admin/scheduler/interviewers/${interviewerId}/availability/overrides/${overrideId}`,
      )
      .then((r) => {
        const d = r.data?.data ?? r.data
        return {
          deleted: (d?.deleted ?? true) as boolean,
          warning: (d?.warning ?? null) as string | null,
        }
      }),

  getRevenueReport: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/reports/revenue', { params }).then((r) => r.data),

  getInterviewerReport: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/reports/interviewers', { params }).then((r) => r.data),

  getAuditLog: (params?: Record<string, unknown>) =>
    apiClient.get('/admin/security/audit-log', { params }).then((r) => r.data),

  exportAuditLog: () =>
    apiClient.get('/admin/security/audit-log/export', { responseType: 'blob' }).then((r) => r.data),

  getSettings: () =>
    apiClient.get('/admin/settings').then((r) => {
      // Interceptor already unwraps DataResponse envelope: r.data = [{key, value}, ...]
      const items: { key: string; value: string }[] = Array.isArray(r.data) ? r.data : []
      return items.reduce<Record<string, string | boolean>>((acc, item) => {
        // Convert "true"/"false" strings back to booleans for toggle fields
        const boolKeys = ['autoRecording', 'emailNotifications', 'smsNotifications', 'twoFactorAuth']
        acc[item.key] = boolKeys.includes(item.key) ? item.value === 'true' : item.value
        return acc
      }, {})
    }),

  updateSettings: (settings: { key: string; value: string }[]) =>
    apiClient.put('/admin/settings', { settings }).then((r) => r.data),

  getIntegrationStatus: () =>
    apiClient.get('/admin/settings/integrations').then((r) => r.data?.integrations ?? []),

  getPublicSettings: () =>
    apiClient.get('/admin/settings/public').then((r) => r.data as Record<string, string>),

  getSiteInfo: () =>
    apiClient.get('/admin/settings/site-info').then((r) => r.data as Record<string, string>),

  getQuestions: (params?: Record<string, unknown>) =>
    apiClient.get('/questions', { params }).then((r) => r.data.data ?? r.data),

  createQuestion: (data: Record<string, unknown>) =>
    apiClient.post('/questions', data).then((r) => r.data.data ?? r.data),

  updateQuestion: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/questions/${id}`, data).then((r) => r.data.data ?? r.data),

  deleteQuestion: (id: number) =>
    apiClient.delete(`/questions/${id}`).then((r) => r.data),

  // Question Types CRUD (admin only)
  getQuestionTypes: (includeInactive = false) =>
    apiClient
      .get('/question-types', { params: { admin: includeInactive } })
      .then((r) => (r.data.data ?? r.data) as QuestionTypeItem[]),

  createQuestionType: (data: { name: string; description?: string; display_order?: number }) =>
    apiClient.post('/question-types', data).then((r) => (r.data.data ?? r.data) as QuestionTypeItem),

  updateQuestionType: (
    id: number,
    data: { name?: string; description?: string; display_order?: number; is_active?: boolean },
  ) =>
    apiClient.put(`/question-types/${id}`, data).then((r) => (r.data.data ?? r.data) as QuestionTypeItem),

  deleteQuestionType: (id: number) =>
    apiClient.delete(`/question-types/${id}`).then((r) => r.data),

  reorderQuestionTypes: (orderedIds: number[]) =>
    apiClient
      .patch('/question-types/reorder', { ordered_ids: orderedIds })
      .then((r) => (r.data.data ?? r.data) as QuestionTypeItem[]),

  // Question Type Categories (5 per type by default; admin-editable)
  getQuestionTypeCategories: (typeId: number, includeInactive = false) =>
    apiClient
      .get(`/question-types/${typeId}/categories`, { params: { active_only: !includeInactive } })
      .then((r) => (r.data.data ?? r.data) as QuestionTypeCategoryItem[]),

  createQuestionTypeCategory: (
    typeId: number,
    data: { key: string; label: string; description: string; display_order?: number },
  ) =>
    apiClient
      .post(`/question-types/${typeId}/categories`, data)
      .then((r) => (r.data.data ?? r.data) as QuestionTypeCategoryItem),

  updateQuestionTypeCategory: (
    catId: number,
    data: {
      key?: string
      label?: string
      description?: string
      display_order?: number
      is_active?: boolean
    },
  ) =>
    apiClient
      .put(`/question-types/categories/${catId}`, data)
      .then((r) => (r.data.data ?? r.data) as QuestionTypeCategoryItem),

  deleteQuestionTypeCategory: (catId: number) =>
    apiClient.delete(`/question-types/categories/${catId}`).then((r) => r.data),

  reorderQuestionTypeCategories: (typeId: number, orderedIds: number[]) =>
    apiClient
      .patch(`/question-types/${typeId}/categories/reorder`, { ordered_ids: orderedIds })
      .then((r) => (r.data.data ?? r.data) as QuestionTypeCategoryItem[]),

  getEvaluations: (params?: { status?: string; page?: number; per_page?: number }) =>
    apiClient.get('/evaluations', { params: { per_page: 20, ...params } }).then((r) => r.data),

  getNeedsEvaluation: (params?: { page?: number; per_page?: number }) =>
    apiClient.get('/evaluations/needs-evaluation', { params: { per_page: 20, ...params } }).then((r) => r.data),

  getAdminAll: (params?: { page?: number; per_page?: number }) =>
    apiClient.get('/evaluations/admin-all', { params: { per_page: 20, ...params } }).then((r) => r.data),

  adminInitEvaluation: (data: { session_id?: number; booking_id?: number }) =>
    apiClient.post('/evaluations/admin-init', data).then((r) => r.data),

  // RBAC
  getRoles: () =>
    apiClient.get('/admin/rbac/roles').then((r) => r.data as RBACRole[]),

  createRole: (data: { name: string; description?: string; permissions: RBACPermission[] }) =>
    apiClient.post('/admin/rbac/roles', data).then((r) => r.data as RBACRole),

  updateRole: (id: number, data: { name?: string; description?: string; permissions?: RBACPermission[] }) =>
    apiClient.put(`/admin/rbac/roles/${id}`, data).then((r) => r.data as RBACRole),

  deleteRole: (id: number) =>
    apiClient.delete(`/admin/rbac/roles/${id}`).then((r) => r.data),

  getAdminUsersWithRoles: () =>
    apiClient.get('/admin/rbac/admin-users').then((r) => r.data as AdminUserRoleItem[]),

  assignUserRole: (userId: number, roleId: number | null) =>
    apiClient.put(`/admin/rbac/admin-users/${userId}/role`, { role_id: roleId }).then((r) => r.data as AdminUserRoleItem),

  getMyPermissions: () =>
    apiClient.get('/admin/rbac/me/permissions').then((r) => r.data as MyPermissions),

  // Email Templates
  getEmailTemplates: () =>
    apiClient.get('/admin/email-templates').then((r) => r.data.data ?? r.data),

  getEmailTemplate: (id: number) =>
    apiClient.get(`/admin/email-templates/${id}`).then((r) => r.data.data ?? r.data),

  updateEmailTemplate: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/admin/email-templates/${id}`, data).then((r) => r.data.data ?? r.data),

  // Terms & Privacy — admin CRUD
  getTermsPolicies: () =>
    apiClient.get('/admin/terms-privacy').then((r) => r.data.data ?? r.data),

  getTermsPolicy: (id: number) =>
    apiClient.get(`/admin/terms-privacy/${id}`).then((r) => r.data.data ?? r.data),

  updateTermsPolicy: (id: number, data: Record<string, unknown>) =>
    apiClient.put(`/admin/terms-privacy/${id}`, data).then((r) => r.data.data ?? r.data),

  // Public — client booking page
  getPublicTermsPolicies: () =>
    apiClient.get('/admin/public/terms-privacy').then((r) => r.data.data ?? r.data),

  // Pay Tiers
  getPayTiers: () =>
    apiClient.get('/admin/pay-tiers').then((r) => (r.data.data ?? r.data) as PayTierItem[]),

  createPayTier: (data: { name: string; label: string; rate: number; description?: string; sort_order?: number }) =>
    apiClient.post('/admin/pay-tiers', data).then((r) => (r.data.data ?? r.data) as PayTierItem),

  updatePayTier: (id: number, data: { label?: string; rate?: number; description?: string; sort_order?: number }) =>
    apiClient.put(`/admin/pay-tiers/${id}`, data).then((r) => (r.data.data ?? r.data) as PayTierItem),

  deletePayTier: (id: number) =>
    apiClient.delete(`/admin/pay-tiers/${id}`).then((r) => r.data),
}
